/**
 * The whole application, as far as a framed document is concerned.
 *
 * `main.tsx` mounts this instead of `App` when the document is inside another
 * page's frame (`IS_EMBED_DOCUMENT`). The edge lets other sites frame exactly
 * one URL shape, `/a/<slug>?embed=true`, but it cannot tell whether that arche
 * may be embedded — so this tree is where that is decided, and it is built so
 * nothing else CAN render:
 *
 * - one route. An embeddable arche at `/a/:id?embed=true`; every other path,
 *   including anywhere a link or a plugin navigation leads, is the refusal.
 *   The app's other routes are not in this tree at all, so there is nothing to
 *   escape to.
 * - embeddable means public AND read-only (`metadata.isEmbeddable`, decided by
 *   the server) AND a plugin. Internal arches have never had an embed view.
 * - signed out, always. `sessionCredentials()` is `'omit'` in a framed
 *   document, so no request from this tree carries the visitor's cookies.
 *   Nothing here signs the visitor out: their session in their own tabs is
 *   untouched, it just never reaches the frame.
 */
import { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { ThemeProvider } from '@/components/theme-provider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/auth';
import api, { type ArcheMetadata } from '@/lib/api';
import { ArcheLayout } from '../components/ArcheLayout';
import { ArcheLoadingScreen } from '../components/ArcheLoadingScreen';
import { PluginRenderer } from '../components/PluginRenderer';
import { shareOrigin } from '@/lib/portal-host';

export function EmbedApp() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="fias-theme" attribute="class">
      {/* PluginRenderer reads auth state; with no credentials sent it always
          resolves signed out. */}
      <AuthProvider>
        <Toaster />
        <Routes>
          <Route path="/a/:id" element={<EmbedArchePage />} />
          <Route path="*" element={<EmbedRefused />} />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}

type EmbedState =
  | { kind: 'loading' }
  | { kind: 'ready'; metadata: ArcheMetadata }
  | { kind: 'refused' };

function EmbedArchePage() {
  const { id: archeId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('embed') === 'true';
  const [state, setState] = useState<EmbedState>({ kind: 'loading' });

  useEffect(() => {
    if (!archeId || !requested) {
      setState({ kind: 'refused' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    api
      .getArcheMetadata(archeId)
      .then((metadata) => {
        if (cancelled) return;
        const embeddable = metadata.isEmbeddable && metadata.renderMode === 'plugin';
        setState(embeddable ? { kind: 'ready', metadata } : { kind: 'refused' });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'refused' });
      });
    return () => {
      cancelled = true;
    };
  }, [archeId, requested]);

  if (state.kind === 'loading') return <ArcheLoadingScreen />;
  if (state.kind === 'refused') return <EmbedRefused />;

  const { metadata } = state;
  return (
    <ArcheLayout displayMode="embed" colorScheme={metadata.colorScheme}>
      <PluginRenderer archeId={metadata.archId} isTrusted={metadata.isTrusted} />
    </ArcheLayout>
  );
}

/**
 * Shown for anything this frame may not display. Offers the page in a new
 * tab — top-level, where the visitor's own session and every normal check
 * apply — and says nothing about why (a private arche and a missing one look
 * the same).
 */
function EmbedRefused() {
  const location = useLocation();
  const openUrl = new URL(location.pathname, shareOrigin()).toString();
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <Card className="max-w-md mx-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Can&apos;t be shown here
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground">
            Only public, read-only arches can be embedded in other sites.
          </p>
          <a
            href={openUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-4"
          >
            Open on Fias
          </a>
        </CardContent>
      </Card>
    </div>
  );
}
